import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add, Remove, Delete } from "@mui/icons-material";
import { addNewRestaurantInvoice } from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import { useNavigate } from "react-router-dom";
import {
  listAllMenuItems,
  resetAllMenuItems,
} from "../../../store/actions/menuItemActions";

const TAX_RATE = 0.025; 
const INITIAL_ITEMS_TO_SHOW = 8;

const formatPrice = (amount) => `₹${Number(amount || 0).toFixed(2)}`;

const generateInvoiceNumber = () => {
  const today = new Date();
  const datePart = today.toISOString().slice(0, 10).replace(/-/g, "");
  const randomSuffix = Math.floor(100 + Math.random() * 900);
  return `INV-${datePart}-${randomSuffix}`;
};

// ===================================================================
// MENU GRID COMPONENT
// ===================================================================
const MenuGrid = ({
  items = [],
  isLoading,
  searchTerm,
  selectedCategory,
  onAddItem,
  onShowMore,
  showAll,
  colors,
}) => {
  const displayed = showAll ? items : items.slice(0, INITIAL_ITEMS_TO_SHOW);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No menu items found. Try adjusting filters.
      </Alert>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        {displayed.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item._id}>
            <Card
              sx={{
                cursor: "pointer",
                transition: "0.2s",
                "&:hover": { boxShadow: 6, transform: "translateY(-2px)" },
                bgcolor: colors.primary[400],
              }}
              onClick={() => onAddItem(item)}
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {item.item_name || "Unnamed Item"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.category || "Uncategorized"}
                </Typography>
                <Typography variant="h6" mt={1} color="primary">
                  {formatPrice(item.price)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {items.length > INITIAL_ITEMS_TO_SHOW && !showAll && (
        <Box textAlign="center" mt={3}>
          <Button variant="outlined" onClick={onShowMore}>
            View More ({items.length - INITIAL_ITEMS_TO_SHOW} more)
          </Button>
        </Box>
      )}
    </>
  );
};

// ===================================================================
// ORDER SUMMARY TABLE
// ===================================================================
const OrderSummary = ({ items = [], onQuantityChange, onRemove }) => {
  if (!items.length) return null;

  return (
    <Box mt={4}>
      <Typography variant="h6" mb={2}>
        Order Summary
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="center">Qty</TableCell>
            <TableCell align="right">Rate</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="center">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item._id}>
              <TableCell>{item.item_name}</TableCell>
              <TableCell align="center">
                <Stack
                  direction="row"
                  spacing={0.5}
                  justifyContent="center"
                  alignItems="center"
                >
                  <IconButton
                    size="small"
                    onClick={() => onQuantityChange(item._id, -1)}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                  <Typography sx={{ minWidth: 24, textAlign: "center" }}>
                    {item.quantity}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onQuantityChange(item._id, 1)}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Stack>
              </TableCell>
              <TableCell align="right">{formatPrice(item.price)}</TableCell>
              <TableCell align="right">
                {formatPrice(item.price * item.quantity)}
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemove(item._id)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// ===================================================================
// MAIN PAGE
// ===================================================================
const AddRestaurantBillPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // ———————————————————————— REDUX ————————————————————————
  const { isLoading, menuItemsList = [] } = useSelector((state) => {
    const menuState = state.menuItems?.menuItems || {};
    return {
      isLoading: state.loading,
      menuItemsList: menuState.list || [],
    };
  });

  // ———————————————————————— LOCAL STATE ————————————————————————
  // const [invNo, setInvNo] = useState("");
  const [invDate] = useState(Date.now());
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [remarks, setRemarks] = useState("");
  const [pmtStatus, setPmtStatus] = useState("unpaid");
  const [active, setActive] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // ———————————————————————— EFFECTS ————————————————————————
  useEffect(() => {
    document.title = "Add Restaurant Bill";
    dispatch(resetAllMenuItems());
    dispatch(listAllMenuItems({ page_number: 1, page_size: 200 }));
  }, [dispatch]);

  // useEffect(() => {
  //   setInvNo(generateInvoiceNumber());
  // }, []);

  // ———————————————————————— MEMOIZED DATA ————————————————————————
  const categories = useMemo(() => {
    const cats = menuItemsList.map((i) => i.category).filter(Boolean);
    return ["All", ...new Set(cats)];
  }, [menuItemsList]);

  const filteredMenu = useMemo(() => {
    return menuItemsList.filter((item) => {
      if (!item.status) return false;
      const matchesCat =
        selectedCategory === "All" || item.category === selectedCategory;
      const matchesSearch = (item.item_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [menuItemsList, selectedCategory, searchTerm]);

  // ———————————————————————— ITEM HANDLERS ————————————————————————
  const handleAddItem = (item) => {
    if (!item?._id) return;
    setSelectedItems((prev) => {
      const existing = prev.find((i) => i._id === item._id);
      if (existing) {
        return prev.map((i) =>
          i._id === item._id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const handleQuantityChange = (id, delta) => {
    setSelectedItems((prev) =>
      prev
        .map((item) =>
          item._id === id
            ? { ...item, quantity: Math.max(1, item.quantity + delta) }
            : item
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const handleRemoveItem = (id) => {
    setSelectedItems((prev) => prev.filter((item) => item._id !== id));
  };

  // ———————————————————————— TOTALS ————————————————————————
  const { subtotal, sgst, cgst, total } = useMemo(() => {
    const sub = selectedItems.reduce(
      (acc, item) => acc + (item.price || 0) * item.quantity,
      0
    );
    const s = sub * TAX_RATE;
    const c = sub * TAX_RATE;
    return { subtotal: sub, sgst: s, cgst: c, total: sub + s + c };
  }, [selectedItems]);

  // ———————————————————————— SUBMIT ————————————————————————
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customerName.trim()) {
      alert("Customer name is required");
      return;
    }

    const billData = {
      customer_name: customerName.trim(),
      customer_mobile: customerMobile.trim(),
      // inv_no: invNo,
      inv_date: invDate,
      items: selectedItems.map((i) => ({
        menu_id: i._id,
        item_name: i.item_name,
        quantity: i.quantity,
        rate: i.price,
        amount: i.price * i.quantity,
      })),
      amount: subtotal,
      sgst,
      cgst,
      total,
      pmt_status: pmtStatus,
      remarks: remarks.trim(),
      active,
    };

    try {
      const savedBillId = await dispatch(addNewRestaurantInvoice(billData));
      navigate(`/billing/restaurants/${savedBillId}`);
    } catch (err) {
      console.error("Failed to save invoice:", err);
      alert("Failed to save invoice. Please try again.");
    }
  };

  // ———————————————————————— RENDER ————————————————————————
  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 1200,
        mx: "auto",
        mt: 5,
        bgcolor: colors.primary[400],
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography variant="h5" fontWeight={600} mb={3}>
        Add Restaurant Invoice
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Invoice Info */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {/* <TextField label="Invoice No" value={invNo} fullWidth disabled /> */}
            <TextField
              label="Customer Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Mobile"
              value={customerMobile}
              onChange={(e) => setCustomerMobile(e.target.value)}
              fullWidth
            />
          </Stack>

          <Divider />

          {/* Menu Section */}
          <Box>
            <Typography variant="h6" mb={2}>
              Select Menu Items
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              mb={2}
              alignItems="center"
              sx={{
                flexWrap: { xs: "wrap", sm: "nowrap" }, // wrap only on xs, never on sm+
                gap: { xs: 1, sm: 2 },
              }}
            >
              {/* Category Dropdown */}
              <TextField
                select
                label="Category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                sx={{ minWidth: 150, flexShrink: 0 }}
                SelectProps={{ native: true }}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </TextField>

              {/* Search Field – takes remaining space */}
              <TextField
                label="Search item"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                sx={{
                  flex: 1,
                  minWidth: { xs: "100%", sm: 250 }, // full width on mobile, min on larger
                }}
              />
            </Stack>

            <MenuGrid
              items={filteredMenu}
              isLoading={isLoading}
              onAddItem={handleAddItem}
              onShowMore={() => setShowAll(true)}
              showAll={showAll}
              colors={colors}
            />
          </Box>

          {/* Order Summary */}
          <OrderSummary
            items={selectedItems}
            onQuantityChange={handleQuantityChange}
            onRemove={handleRemoveItem}
          />

          {/* Totals */}
          {selectedItems.length > 0 && (
            <Box
              textAlign="right"
              p={2}
              bgcolor={colors.primary[400]}
              borderRadius={1}
            >
              <Typography>Subtotal: {formatPrice(subtotal)}</Typography>
              <Typography>SGST (2.5%): {formatPrice(sgst)}</Typography>
              <Typography>CGST (2.5%): {formatPrice(cgst)}</Typography>
              <Typography variant="h6" fontWeight={600} mt={1}>
                Total: {formatPrice(total)}
              </Typography>
            </Box>
          )}

          {/* Payment & Remarks */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              select
              label="Payment Status"
              value={pmtStatus}
              onChange={(e) => setPmtStatus(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </TextField>
            <TextField
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>

          {/* <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label={active ? "Active" : "Inactive"}
          /> */}

          <Button
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            fullWidth
            disabled={isLoading || selectedItems.length === 0}
          >
            {isLoading ? "Saving..." : "Submit Invoice"}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default AddRestaurantBillPage;
